import { useState, useEffect } from "react";
import { Table, Button, message, Dropdown, Modal, Tag } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getAllProjects,
  Project,
  toggleFeatured,
  toggleVisibility,
} from "../services/projectService";
import dayjs from "dayjs";
import type { MenuProps } from "antd";

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch projects on mount and when pagination changes
  useEffect(() => {
    loadProjects();
  }, [pagination.current, pagination.pageSize]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await getAllProjects({
        page: pagination.current,
        limit: pagination.pageSize,
      });

      if (response.success) {
        setProjects(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.totalItems || 0,
        }));
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFeatured = (project: Project) => {
    const newStatus = project.isFeatured ? "not featured" : "featured";

    Modal.confirm({
      title: "Change Featured Status",
      content: `Are you sure you want to mark this project as ${newStatus}?`,
      okText: "Yes, Change",
      cancelText: "Cancel",
      okButtonProps: {
        className: "!bg-[#010ED0] hover:bg-[#010ED0]/80 border-0",
      },
      cancelButtonProps: {
        className: "border-gray-300 text-gray-700 hover:border-gray-400",
      },
      onOk: async () => {
        try {
          const response = await toggleFeatured(project._id);
          if (response.success) {
            message.success(`Project ${newStatus} successfully`);
            loadProjects(); // Reload projects to reflect changes
          } else {
            message.error(
              response.message || "Failed to update featured status"
            );
          }
        } catch (error: any) {
          message.error(
            error.response?.data?.message || "Failed to update featured status"
          );
        }
      },
    });
  };

  const handleToggleVisibility = (project: Project) => {
    const newStatus = project.isVisible ? "hidden" : "visible";

    Modal.confirm({
      title: "Change Visibility Status",
      content: `Are you sure you want to make this project ${newStatus}?`,
      okText: "Yes, Change",
      cancelText: "Cancel",
      okButtonProps: {
        className: "!bg-[#010ED0] hover:bg-[#010ED0]/80 border-0",
      },
      cancelButtonProps: {
        className: "border-gray-300 text-gray-700 hover:border-gray-400",
      },
      onOk: async () => {
        try {
          const response = await toggleVisibility(project._id);
          if (response.success) {
            message.success(`Project ${newStatus} successfully`);
            loadProjects(); // Reload projects to reflect changes
          } else {
            message.error(
              response.message || "Failed to update visibility status"
            );
          }
        } catch (error: any) {
          message.error(
            error.response?.data?.message ||
              "Failed to update visibility status"
          );
        }
      },
    });
  };

  const columns = [
    {
      title: "Project Name",
      dataIndex: "title",
      key: "title",
      render: (text: string) => (
        <span className="text-gray-900 font-medium">
          {text.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
    },
    {
      title: "Owner Name",
      dataIndex: "ownerName",
      key: "ownerName",
      render: (text: string) => (
        <span className="text-gray-600">{text || "N/A"}</span>
      ),
    },
    {
      title: "Owner Email",
      dataIndex: "ownerEmail",
      key: "ownerEmail",
      render: (text: string) => (
        <span className="text-gray-600">{text || "N/A"}</span>
      ),
    },
    {
      title: "Industry",
      dataIndex: "industry",
      key: "industry",
      render: (text: string) => {
        if (!text) return <span className="text-gray-600">N/A</span>;
        
        // Split by comma and trim each industry
        const industries = text.split(",").map((ind) => ind.trim()).filter((ind) => ind.length > 0);
        
        if (industries.length === 0) {
          return <span className="text-gray-600">N/A</span>;
        }
        
        if (industries.length === 1) {
          return <span className="text-gray-600">{industries[0]}</span>;
        }
        
        // Show first industry + remaining count
        const remaining = industries.length - 1;
        return (
          <span className="text-gray-600">
            {industries[0]} <span className="text-gray-400">+{remaining} more</span>
          </span>
        );
      },
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
      render: (tag: string) => {
        if (!tag) return <span className="text-gray-400">N/A</span>;
        return (
          <Tag
            color={tag === "hackathon" ? "blue" : "purple"}
            className="rounded-full px-3 py-1 text-xs font-medium"
          >
            {tag === "hackathon" ? "Hackathon" : "Internal"}
          </Tag>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date: string) => (
        <span className="text-sm text-gray-600">
          {date ? dayjs(date).format("YYYY-MM-DD HH:mm") : "N/A"}
        </span>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, record: Project) => (
        <div className="flex gap-2">
          {record.isFeatured && (
            <span className="px-2 py-1 border border-[#B9E6FE] bg-[#F0F9FF] text-[#026AA2] rounded-full text-xs font-medium">
              Featured
            </span>
          )}
          {record.isVisible ? (
            <span className="px-2 py-1 border border-[#ABEFC6] bg-[#ECFDF3] text-[#067647] rounded-full text-xs font-medium">
              Visible
            </span>
          ) : (
            <span className="px-2 py-1 border border-[#E2E8F0] bg-[#F8FAFC] text-[#475569] rounded-full text-xs font-medium">
              Hidden
            </span>
          )}
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: 80,
      render: (_: any, record: Project) => {
        const menuItems: MenuProps["items"] = [
          {
            key: "view",
            label: "View Details",
            onClick: () => navigate(`/projects/${record._id}`),
          },
          {
            key: "edit",
            label: "Edit Project",
            onClick: () => navigate(`/projects/edit/${record._id}`),
          },
          {
            key: "featured",
            label: record.isFeatured
              ? "Remove from Featured"
              : "Mark as Featured",
            onClick: () => handleToggleFeatured(record),
          },
          {
            key: "visibility",
            label: record.isVisible ? "Hide Project" : "Show Project",
            onClick: () => handleToggleVisibility(record),
          },
        ];

        return (
          <Dropdown menu={{ items: menuItems }} placement="bottomRight">
            <Button
              type="text"
              icon={<MoreOutlined />}
              className="text-gray-500 hover:text-gray-700"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
        <button
          onClick={() => navigate("/projects/add")}
          className="bg-[#010ED0] hover:bg-[#010ED0]/80 flex items-center gap-2 px-4 py-2 rounded-md text-white"
        >
          <PlusOutlined />
          <span>Add Project</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1">
        <Table
          columns={columns}
          dataSource={projects}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize || 10,
              }));
            },
          }}
          className="projects-table"
          rowClassName="hover:bg-gray-50"
        />
      </div>
    </div>
  );
};

export default Projects;
